/**
 * SmsKurulumu — Profil > Güvenlik: SMS ile doğrulamayı kur (backlog #640, backend #639).
 *
 *   1) Numara: profildeki numara (maskeli gösterilir; sunucuya numara GİTMEZ, `phone` yoksa sunucu profildekini kullanır) ya da başka bir numara
 *      (0532 123 45 67 gibi; sunucu kuralı: Türkiye cep numarası). İkinci faktör zaten açıksa MEVCUT ŞİFRE de ister.
 *   2) Kod: SMS ile gelen kod yazılır → /mfa/sms/confirm. Yanlış kod kalan hakkı söyler; 5. yanlış kodu öldürür ve kilitler (429).
 *      "Yeniden gönder" sunucunun bekleme süresini (`resendAfterSeconds`, ya da 429'da `retryAfterSeconds`) geri sayar.
 * Şifre yalnız bu bileşenin belleğinde durur (yeniden gönderme aynı şifreyi ister) ve bileşen kapanınca atılır.
 */
import React, { useState } from 'react';
import { Alert, Button, Form, Input, Label, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import useGeriSayim from '../../../Components/Common/useGeriSayim';
import { smsKurulumuBaslat, smsKurulumunuOnayla } from '../../../services/mfaService';
import { mfaMesaji } from './mfaMesajlari';

const SmsKurulumu = ({ durum, onBitti, onIptal }) => {
  const { t } = useTranslation('giris');
  const sifreGerekli = Boolean(durum && durum.mfaEnabled);
  const profilNumarasi = Boolean(durum && durum.userPhoneUsable);

  const [baskaNumara, setBaskaNumara] = useState(!profilNumarasi);
  const [numara, setNumara] = useState('');
  const [sifre, setSifre] = useState('');
  const [adim, setAdim] = useState('numara');   // 'numara' | 'kod'
  const [gonderilen, setGonderilen] = useState(null);   // { phoneMasked }
  const [kod, setKod] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [kilitli, setKilitli] = useState(false);
  const [kalan, geriSayimiBaslat] = useGeriSayim(0);

  async function kodGonder(e) {
    if (e) e.preventDefault();
    setYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await smsKurulumuBaslat({ phone: baskaNumara ? numara : undefined, password: sifreGerekli ? sifre : undefined });
      if (sonuc.ok) {
        setGonderilen({ phoneMasked: sonuc.data.phoneMasked });
        geriSayimiBaslat(sonuc.data.resendAfterSeconds || 60);
        setKod('');
        setAdim('kod');
        return;
      }
      if (sonuc.kind === 'TOO_SOON' && sonuc.retryAfterSeconds) geriSayimiBaslat(sonuc.retryAfterSeconds);
      if (sonuc.kind === 'LOCKED') setKilitli(true);
      setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYukleniyor(false);
    }
  }

  async function onayla(e) {
    e.preventDefault();
    const temiz = kod.replace(/\s+/g, '');
    if (!temiz) return;
    setYukleniyor(true);
    setHata(null);
    try {
      const sonuc = await smsKurulumunuOnayla(temiz);
      if (sonuc.ok) { onBitti(); return; }
      if (sonuc.kind === 'LOCKED') setKilitli(true);
      setKod('');
      setHata(mfaMesaji(t, sonuc));
    } catch {
      setHata(t('mfa.hata.baglanti'));
    } finally {
      setYukleniyor(false);
    }
  }

  if (adim === 'kod') {
    return (
      <Form onSubmit={onayla} data-testid="sms-kod-adimi">
        <p className="text-muted">{t('mfa.guvenlik.smsKodGitti', { numara: gonderilen ? gonderilen.phoneMasked : '' })}</p>
        {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
        <div className="mb-3">
          <Label htmlFor="sms-kurulum-kod" className="form-label">{t('mfa.giris.kodSms')}</Label>
          <Input id="sms-kurulum-kod" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={10}
            value={kod} disabled={kilitli || yukleniyor} onChange={(e) => setKod(e.target.value)} />
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button color="primary" type="submit" disabled={yukleniyor || kilitli || !kod.trim()}>{yukleniyor ? <Spinner size="sm" /> : t('mfa.guvenlik.onayla')}</Button>
          <Button color="light" type="button" id="sms-kurulum-yeniden" disabled={kalan > 0 || yukleniyor || kilitli} onClick={() => kodGonder()}>
            {kalan > 0 ? t('mfa.giris.yenidenGonderBekle', { saniye: kalan }) : t('mfa.giris.yenidenGonder')}
          </Button>
          <Button color="link" type="button" className="text-muted" onClick={onIptal}>{t('mfa.guvenlik.vazgec')}</Button>
        </div>
      </Form>
    );
  }

  return (
    <Form onSubmit={kodGonder} data-testid="sms-numara-adimi">
      <p className="text-muted">{t('mfa.guvenlik.smsKurAciklama')}</p>
      {hata && <Alert color="danger" fade={false}>{hata}</Alert>}
      {profilNumarasi && (
        <div className="mb-3">
          <div className="form-check">
            <Input className="form-check-input" type="radio" name="sms-numara-kaynagi" id="sms-profil-numarasi" checked={!baskaNumara} onChange={() => setBaskaNumara(false)} />
            <Label className="form-check-label" htmlFor="sms-profil-numarasi">{t('mfa.guvenlik.profilNumarasi', { numara: durum.userPhoneMasked || '' })}</Label>
          </div>
          <div className="form-check">
            <Input className="form-check-input" type="radio" name="sms-numara-kaynagi" id="sms-baska-numara" checked={baskaNumara} onChange={() => setBaskaNumara(true)} />
            <Label className="form-check-label" htmlFor="sms-baska-numara">{t('mfa.guvenlik.baskaNumara')}</Label>
          </div>
        </div>
      )}
      {!profilNumarasi && <p className="small text-muted">{t('mfa.guvenlik.profilNumarasiYok')}</p>}
      {baskaNumara && (
        <div className="mb-3">
          <Label htmlFor="sms-kurulum-numara" className="form-label">{t('mfa.guvenlik.cepNumarasi')}</Label>
          <Input id="sms-kurulum-numara" type="tel" inputMode="tel" autoComplete="tel" placeholder="0532 123 45 67" value={numara} onChange={(e) => setNumara(e.target.value)} />
        </div>
      )}
      {sifreGerekli && (
        <div className="mb-3">
          <Label htmlFor="sms-kurulum-sifre" className="form-label">{t('mfa.guvenlik.mevcutSifre')}</Label>
          <Input id="sms-kurulum-sifre" type="password" autoComplete="current-password" value={sifre} onChange={(e) => setSifre(e.target.value)} />
          <div className="form-text">{t('mfa.guvenlik.sifreNedeni')}</div>
        </div>
      )}
      <div className="d-flex gap-2">
        <Button color="primary" type="submit" disabled={yukleniyor || kalan > 0 || (baskaNumara && !numara.trim()) || (sifreGerekli && !sifre)}>
          {yukleniyor ? <Spinner size="sm" /> : kalan > 0 ? t('mfa.giris.yenidenGonderBekle', { saniye: kalan }) : t('mfa.guvenlik.kodGonder')}
        </Button>
        <Button color="light" type="button" onClick={onIptal}>{t('mfa.guvenlik.vazgec')}</Button>
      </div>
    </Form>
  );
};

export default SmsKurulumu;
